import * as moment from 'moment';

/**
 * Single source of truth for the Daily diary list filters.
 *
 * The chips (`app-daily-diary-filters`), the horizontal date strip
 * (`app-daily-diary-wrapper`) and the list (`app-daily-diaries-list`) are three
 * separate components that all read the same query params, so the defaults have
 * to be resolved in one place. If the chip and the list disagreed about what
 * "no params" means you get the classic silently-empty list.
 *
 * Query params:
 *   diaryStart / diaryEnd  explicit YYYY-MM-DD window
 *   diaryRange=all         explicit unbounded ("All Time")
 *   diaryStatus            one or more status values; absent = every status
 */

/** Default window: the last two weeks, inclusive of today. */
export const DIARY_DEFAULT_RANGE_DAYS = 14;

/** Sentinel for the explicit "All Time" state. */
export const DIARY_RANGE_ALL = 'all';

export const DIARY_STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: 'Draft', value: 'draft' },
  { label: 'Submitted', value: 'submitted' },
];

export interface DiaryDateWindow {
  /** '' means unbounded — send no bound to the api. */
  startDate: string;
  endDate: string;
  /** True when the window is the untouched default (chip shows no clear X). */
  isDefault: boolean;
  /** True for the explicit All Time state. */
  isAllTime: boolean;
}

export function defaultDiaryDateWindow(): { startDate: string; endDate: string } {
  return {
    startDate: moment().subtract(DIARY_DEFAULT_RANGE_DAYS, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
  };
}

export function resolveDiaryDateWindow(queryParams: any): DiaryDateWindow {
  if (queryParams?.['diaryRange'] === DIARY_RANGE_ALL) {
    return { startDate: '', endDate: '', isDefault: false, isAllTime: true };
  }

  const start = asApiDate(queryParams?.['diaryStart']);
  const end = asApiDate(queryParams?.['diaryEnd']);

  if (start && end) {
    const fallback = defaultDiaryDateWindow();
    return {
      startDate: start,
      endDate: end,
      isDefault: start === fallback.startDate && end === fallback.endDate,
      isAllTime: false,
    };
  }

  return { ...defaultDiaryDateWindow(), isDefault: true, isAllTime: false };
}

/**
 * Effective status filter. An empty result means "every status" — the default,
 * and also where an empty or fully-selected checkbox list lands, so clearing
 * the filter can never produce an empty list.
 */
export function resolveDiaryStatuses(queryParams: any): string[] {
  const requested = asArray(queryParams?.['diaryStatus'])
    .filter(value => DIARY_STATUS_OPTIONS.some(option => option.value === value));

  if (!requested.length || requested.length === DIARY_STATUS_OPTIONS.length) return [];
  return requested;
}

export function asArray(value: any): string[] {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

export function asApiDate(value: any): string {
  return value && moment(value, 'YYYY-MM-DD', true).isValid()
    ? moment(value, 'YYYY-MM-DD').format('YYYY-MM-DD')
    : '';
}
